import { LabelBadgeDot } from '../../../shared/components/label-badge/label-badge';
import {
  FeedDiagnostics,
  GtfsDiagnosticsPayload,
  GtfsDiagnosticsStatus,
  GtfsRealtimeState,
  GtfsStaticState,
} from '../../../shared/models/feed-diagnostics.model';
import { GTFS_PROVIDER_TYPE } from './gtfs-config';

/**
 * Display helpers for GTFS diagnostics snapshots. GTFS computes the overall
 * `status`/`message` itself (worst problem wins), so nothing here re-derives
 * health — it only maps states to badge colours and narrows the payload.
 */

const STATUS_DOT: Record<GtfsDiagnosticsStatus, LabelBadgeDot> = {
  OK: 'green',
  DEGRADED: 'orange',
  ERROR: 'red',
  UNKNOWN: 'yellow',
};

const STATIC_DOT: Record<GtfsStaticState, LabelBadgeDot | null> = {
  ACTIVE: 'green',
  IMPORTING: 'yellow',
  FAILED: 'red',
  NONE: null,
};

/** IDLE is yellow (configured but not polling) and DISABLED neutral; GTFS
 *  never lowers the overall status for either. */
const REALTIME_DOT: Record<GtfsRealtimeState, LabelBadgeDot | null> = {
  OK: 'green',
  ERROR: 'red',
  IDLE: 'yellow',
  DISABLED: null,
  UNKNOWN: 'yellow',
};

export function gtfsStatusDot(status: GtfsDiagnosticsStatus): LabelBadgeDot {
  return STATUS_DOT[status] ?? 'yellow';
}

export function gtfsStaticDot(state: GtfsStaticState): LabelBadgeDot | null {
  return STATIC_DOT[state] ?? null;
}

export function gtfsRealtimeDot(state: GtfsRealtimeState): LabelBadgeDot | null {
  return REALTIME_DOT[state] ?? null;
}

/** The snapshot's payload as GTFS diagnostics, or null when it isn't one
 *  (no snapshot, another provider type, or a payload without the GTFS shape). */
export function asGtfsDiagnostics(snapshot: FeedDiagnostics | null | undefined): GtfsDiagnosticsPayload | null {
  if (!snapshot || snapshot.providerType !== GTFS_PROVIDER_TYPE) return null;
  const payload = snapshot.payload as Partial<GtfsDiagnosticsPayload> | null;
  return payload && payload.static && payload.realtime && payload.status ? (payload as GtfsDiagnosticsPayload) : null;
}

/** Whole minutes since the snapshot was generated. */
export function snapshotAgeMinutes(snapshot: FeedDiagnostics): number {
  return Math.max(0, Math.floor((Date.now() - new Date(snapshot.generatedAt).getTime()) / 60_000));
}
